import type { LucideIcon } from 'lucide-react';
import { ClipboardCheck, Presentation, Printer } from 'lucide-react';
import {
  deliveryProfileFromIntent,
  parseDeliveryProfile,
  type DeliveryProfile,
} from './deliveryProfile';
import { buildStudioMaterialUrl } from './studioMaterialRoutes';
import type { LinkedSessionContext } from '@interactive-room';

export type MaterialIntent = 'print' | 'interactive' | 'exam';

export interface MaterialIntentOption {
  id: MaterialIntent;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  hint: string;
}

export const MATERIAL_INTENT_OPTIONS: MaterialIntentOption[] = [
  {
    id: 'print',
    Icon: Printer,
    title: 'Guía de aprendizaje',
    subtitle: 'Teoría + práctica en la misma ficha · imprimible',
    hint: '¿Enseño el contenido mientras practican?',
  },
  {
    id: 'interactive',
    Icon: Presentation,
    title: 'Clase interactiva',
    subtitle: 'Recordatorios + ejercicios · proyectable',
    hint: '¿Repaso con fórmulas y más práctica en vivo?',
  },
  {
    id: 'exam',
    Icon: ClipboardCheck,
    title: 'Evaluación (Sumativa/Formativa)',
    subtitle: 'Solo parte aplicativa · async o ticket',
    hint: '¿Mido lo aprendido sin enseñar de nuevo?',
  },
];

export function materialIntentRoute(intent: MaterialIntent, subjectId: string, courseId?: string): string {
  const profile = deliveryProfileFromIntent(intent);
  return buildStudioMaterialUrl({ profile, subjectId, courseId });
}

/** Estado de navegación tras elegir intención en el wizard de creación global. */
export interface MaterialIntentNavigationState {
  openAiDialog: true;
  materialIntent: MaterialIntent;
  /** Contexto pedagógico inyectado desde una sesión planificada (Contexto en Cascada). */
  linkedSessionContext?: LinkedSessionContext;
  /** Intención docente capturada en StudioCreateModal paso 2. */
  teacherPrompt?: string;
  /** Perfil de flujo para GuideGenerationRouter (scale_project, measurement_inquiry, …). */
  flowProfile?: string;
}

export function materialIntentNavigationState(
  intent: MaterialIntent,
  linkedSessionContext?: LinkedSessionContext,
  extras?: { teacherPrompt?: string; flowProfile?: string },
): MaterialIntentNavigationState {
  const base: MaterialIntentNavigationState = linkedSessionContext
    ? { openAiDialog: true, materialIntent: intent, linkedSessionContext }
    : { openAiDialog: true, materialIntent: intent };

  if (extras?.teacherPrompt) base.teacherPrompt = extras.teacherPrompt;
  if (extras?.flowProfile) base.flowProfile = extras.flowProfile;
  return base;
}

const MATERIAL_INTENTS = new Set<MaterialIntent>(['print', 'interactive', 'exam']);

export function parseMaterialIntent(value: string | null): MaterialIntent | null {
  return value && MATERIAL_INTENTS.has(value as MaterialIntent) ? (value as MaterialIntent) : null;
}

/** Permite editar un exam existente; creación nueva exige pasar por StudioCreateModal. */
export function hasMaterialIntentEntry(
  searchParams: URLSearchParams,
  examId?: string,
): boolean {
  if (examId) return true;
  return parseDeliveryProfile(searchParams.get('intent'), searchParams.get('profile')) !== null;
}

export function readDeliveryProfileFromSearch(
  searchParams: URLSearchParams,
): DeliveryProfile | null {
  return parseDeliveryProfile(searchParams.get('intent'), searchParams.get('profile'));
}
